import { LayoutItem } from 'api/models';
import { FieldTemplate, FieldType } from 'api/models/FieldTemplate';
import {
  LayoutItemStyle,
  LayoutItemSubType,
  LayoutItemType,
} from 'api/models/FormTemplate';
import { getId } from 'utils';

export const DEFAULT_LAYOUT_WIDTH = '12';

interface GetNewLayoutItemProps {
  type: FieldType;
}

export function getNewLayoutItem({ type }: GetNewLayoutItemProps) {
  switch (type) {
    case FieldType.Layout:
      return getNewLayoutContainer();
    case FieldType.Header:
      return getNewLayoutDivider({
        isHeader: true,
      });
    case FieldType.TableSelect:
      return getNewTableSelect();
    default:
    case FieldType.Divider:
      return getNewLayoutDivider({
        isHeader: false,
      });
  }
}

function getNewTableSelect(): LayoutItem {
  const id = getId();

  return {
    id,
    type: LayoutItemType.Container,
    subType: LayoutItemSubType.SelectableTable,
    layoutOptions: {
      layout: '',
    },
    children: [],
  };
}

function getNewLayoutDivider({ isHeader }: { isHeader: boolean }): LayoutItem {
  const id = getId();

  return {
    id,
    type: LayoutItemType.Static,
    subType: isHeader
      ? LayoutItemSubType.Header
      : LayoutItemSubType.HorizontalSeparator,
    layoutOptions: {
      layout: '',
    },
  };
}

function getNewLayoutContainer(): LayoutItem {
  const layoutChildrenRowId = getId();
  const layoutChildrenColumnId = getId();
  const id = getId();

  return {
    id,
    type: LayoutItemType.Container,
    subType: LayoutItemSubType.Layout,
    layoutOptions: {
      layout: DEFAULT_LAYOUT_WIDTH,
    },
    children: [
      {
        id: layoutChildrenRowId,
        type: LayoutItemType.Container,
        subType: LayoutItemSubType.Layout,
        layoutOptions: {
          layout: DEFAULT_LAYOUT_WIDTH,
        },
        children: [
          {
            id: layoutChildrenColumnId,
            type: LayoutItemType.Container,
            subType: LayoutItemSubType.Layout,
            layoutOptions: {
              layout: DEFAULT_LAYOUT_WIDTH,
            },
            children: [],
          },
        ],
      },
    ],
  };
}

export function getNewRowLayoutItem(): LayoutItem {
  const columnId = getId();
  const rowId = getId();
  return {
    id: rowId,
    type: LayoutItemType.Container,
    subType: LayoutItemSubType.Layout,
    layoutOptions: {
      layout: DEFAULT_LAYOUT_WIDTH,
      style: {
        justifyContent: 'start',
      },
    },
    children: [
      {
        id: columnId,
        type: LayoutItemType.Container,
        subType: LayoutItemSubType.Layout,
        layoutOptions: {
          layout: DEFAULT_LAYOUT_WIDTH,
        },
        children: [],
      },
    ],
  };
}

export function getNewColumnLayoutItem(columnWidth: string): LayoutItem {
  const id = getId();
  return {
    id,
    type: LayoutItemType.Container,
    subType: LayoutItemSubType.Layout,
    layoutOptions: {
      layout: columnWidth ?? DEFAULT_LAYOUT_WIDTH,
    },
    children: [],
  };
}

export function getNewInputLayoutItem({
  id,
  fieldTemplateId,
}: {
  id: string;
  fieldTemplateId: string;
}): LayoutItem {
  return {
    id,
    type: LayoutItemType.Input,
    fieldTemplateId: fieldTemplateId,
    layoutOptions: {
      layout: DEFAULT_LAYOUT_WIDTH,
    },
  };
}

export function getNewFieldItem(
  fieldType: FieldType,
  id: string,
  formFieldTemplateId: string,
) {
  return {
    id,
    fieldType: fieldType,
    fieldAttributes: {
      label: '',
    },
    formFieldTemplateId,
    alias: '',
    version: 0,
  };
}

export const getFieldLabel = ({
  field,
  t,
}: {
  field: FieldTemplate;
  t: (key: string) => string;
}) => {
  const { label, isRequired } = field.fieldAttributes ?? {};
  if (label) {
    return isRequired ? `${label} *` : label;
  }
  return t(`forms.fieldType.${field.fieldType}`);
};

export const getLabel = ({
  t,
  label,
  isRequired,
}: {
  label: string;
  isRequired: boolean;
  t: (key: string) => string;
}) => {
  return isRequired ? `${t(label)} *` : t(label);
};

interface getAllFieldTemplateIdsProps {
  fieldTemplateId?: string;
  children?: LayoutItem[];
}

export function getFieldIdsOfLayoutField(
  layoutItem: getAllFieldTemplateIdsProps,
): string[] {
  const fieldTemplateIds: string[] = [layoutItem.fieldTemplateId ?? ''];

  if (layoutItem.children) {
    layoutItem.children.forEach(child => {
      fieldTemplateIds.push(...getFieldIdsOfLayoutField(child));
    });
  }

  return fieldTemplateIds.flat().filter(Boolean);
}

export function getFieldsOfSelectableTable({
  layoutItems,
  fieldTemplates,
}: {
  layoutItems?: LayoutItem[];
  fieldTemplates: FieldTemplate[];
}) {
  return layoutItems
    ?.map(item => {
      const field = fieldTemplates.find(
        field => field.id === item.fieldTemplateId,
      );
      return field;
    })
    .filter(Boolean) as FieldTemplate[];
}

export function getLayoutItemStyles(style?: LayoutItemStyle) {
  const defaultPt = '16px';
  const defaultPlPrPb = '8px';
  const defaultMargin = '0px';
  const defaultBackgroundColor = 'transparent';

  const paddings = {
    paddingLeft:
      style?.backgroundColor && (style?.paddingLeft ?? defaultPlPrPb),
    paddingRight:
      style?.backgroundColor && (style?.paddingRight ?? defaultPlPrPb),
    paddingTop: style?.backgroundColor && (style?.paddingTop ?? defaultPt),
    paddingBottom:
      style?.backgroundColor && (style?.paddingBottom ?? defaultPlPrPb),
  };

  const margins = {
    marginBottom: style?.backgroundColor
      ? style?.marginBottom ?? '10px'
      : style?.marginBottom ?? defaultMargin,
    marginTop: style?.marginTop ?? defaultMargin,
    marginLeft: style?.marginLeft ?? defaultMargin,
    marginRight: style?.marginRight ?? defaultMargin,
  };

  const backgroundColor = {
    backgroundColor: style?.backgroundColor ?? defaultBackgroundColor,
    borderRadius: '5px',
  };

  return {
    ...paddings,
    ...margins,
    ...backgroundColor,
  };
}

export function getIsPathPreceding(path1: string, path2: string) {
  // Split the paths into an array of indexes
  const indexes1 = path1.split('.').slice(1); // Skip leading "children"
  const indexes2 = path2.split('.').slice(1);

  // Loop through the indexes comparing them
  for (let i = 0; i < Math.min(indexes1.length, indexes2.length); i++) {
    const index1 = parseInt(indexes1[i], 10);
    const index2 = parseInt(indexes2[i], 10);

    // If index1 is smaller, path1 precedes path2
    if (index1 < index2) {
      return true;
    } else if (index1 > index2) {
      // If index1 is larger, path1 does not precede path2 (can exit)
      return false;
    }
  }

  // If all indexes are equal, the longer path precedes
  return indexes1.length < indexes2.length;
}

export function isElementFullyVisible(element: HTMLElement) {
  if (!(element instanceof HTMLElement)) {
    throw new Error('Invalid input: Must provide a valid HTML element');
  }

  try {
    const elementRect = element.getBoundingClientRect();
    const viewportWidth =
      window.innerWidth || document.documentElement.clientWidth;
    const viewportHeight =
      window.innerHeight || document.documentElement.clientHeight;
    if (elementRect.width === 0 || elementRect.height === 0) {
      return false;
    }
    const elementStyle = window.getComputedStyle(element);
    if (
      elementStyle.display === 'none' ||
      elementStyle.visibility === 'hidden' ||
      elementStyle.opacity === '0'
    ) {
      return false;
    }

    const isWithinViewport =
      elementRect.top >= 0 &&
      elementRect.left >= 0 &&
      elementRect.bottom <= viewportHeight &&
      elementRect.right <= viewportWidth;

    let currentElement = element;
    while (currentElement && isWithinViewport) {
      const parentStyle = window.getComputedStyle(currentElement);

      if (parentStyle.overflow === 'hidden') {
        const parentRect = currentElement.getBoundingClientRect();
        if (
          elementRect.top < parentRect.top ||
          elementRect.left < parentRect.left ||
          elementRect.bottom > parentRect.bottom ||
          elementRect.right > parentRect.right
        ) {
          return false;
        }
      }

      // @ts-ignore
      currentElement = currentElement.parentElement;
    }

    return isWithinViewport;
  } catch (error) {
    console.error('Error checking element visibility:', error);
    return false;
  }
}
